/**
 * Turns a flat event list into the day sections the agenda renders.
 *
 * Agenda is the view that FLOWS. The hour grid positions blocks by duration
 * inside a fixed-height row, so it clips under a large font scale and wastes
 * most of its height on empty time; a list does neither. That makes it both
 * the view a rep actually reads on a phone and the accessible fallback when
 * text is scaled up.
 *
 * Pure: no React, no pixels.
 */
import type { CalendarEvent } from '../../../types/calendar';
import { compareLocalDate, type LocalDate } from './calendarDate';
import { groupEventsByLocalDay } from './calendarEventBucketing';

export interface AgendaSection {
  date: LocalDate;
  events: CalendarEvent[];
  /** Meetings that still stand — what the header counts. */
  meetingCount: number;
  cancelledCount: number;
}

/**
 * A cancellation is worth showing (a rep needs to know the slot freed up), but
 * it is not a meeting. Anything already in the past — completed, no-show — is
 * neither: it is history, and counting it as "3 meetings today" would be a lie.
 */
const STANDING_STATUSES = new Set<CalendarEvent['status']>(['scheduled', 'confirmed']);

function compareEvents(a: CalendarEvent, b: CalendarEvent): number {
  // All-day events head the day: they have no start time to sort against, and
  // they frame everything under them.
  if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
  return a.startsAt.localeCompare(b.startsAt);
}

export function buildAgendaSections(
  events: readonly CalendarEvent[],
  timeZone: string,
): AgendaSection[] {
  // Days with nothing on them are omitted rather than rendered empty — that
  // dead space is the exact failing of the 24-hour grid this view answers.
  return groupEventsByLocalDay([...events], timeZone)
    .map(({ date, events: dayEvents }) => {
      const sorted = [...dayEvents].sort(compareEvents);

      return {
        date,
        events: sorted,
        meetingCount: sorted.filter((event) => STANDING_STATUSES.has(event.status)).length,
        cancelledCount: sorted.filter((event) => event.status === 'cancelled').length,
      };
    })
    .sort((a, b) => compareLocalDate(a.date, b.date));
}
